import { useState, useRef, useEffect } from "react";
import { useTheme } from "@/hooks/use-theme";
import { useQuery } from "@tanstack/react-query";
import type { PlayerRanking, ClubWithMembership, ClubRanking, Player } from "../../../shared/schema";
import { Medal, Trophy, ChevronDown, Crown, Lock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

// ELO tier thresholds
const ELO_TIERS = [
  { min: 0, max: 1149, name: "beginner", level: 1 },
  { min: 1150, max: 1299, name: "improvement", level: 2 },
  { min: 1300, max: 1399, name: "elementary", level: 3 },
  { min: 1400, max: 1499, name: "intermediate", level: 4 },
  { min: 1500, max: 1599, name: "confirmed", level: 5 },
  { min: 1600, max: 1749, name: "advanced", level: 6 },
  { min: 1750, max: 1999, name: "expert", level: 7 },
  { min: 2000, max: Infinity, name: "elite", level: 8 }
];

function getEloProgress(rating: number) {
  const currentTier = ELO_TIERS.find(tier => rating >= tier.min && rating <= tier.max);
  if (!currentTier) return { currentTier: null, nextTier: null, progress: 0, isMaxLevel: true };

  const nextTier = ELO_TIERS.find(tier => tier.level === currentTier.level + 1);
  if (!nextTier) return { currentTier, nextTier: null, progress: 100, isMaxLevel: true };

  const tierRange = currentTier.max - currentTier.min;
  const currentProgress = rating - currentTier.min;
  const progress = (currentProgress / tierRange) * 100;

  return { currentTier, nextTier, progress: Math.round(progress), isMaxLevel: false };
}

interface RankingTabProps {
  playerId?: string | null;
}

export default function RankingTab({ playerId }: RankingTabProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [selectedClub, setSelectedClub] = useState<string>(localStorage.getItem("rankingSelectedClub") || "global");
  const [timeFilter, setTimeFilter] = useState<string>(localStorage.getItem("rankingTimeFilter") || "all");
  const [isClubDropdownOpen, setIsClubDropdownOpen] = useState<boolean>(false);
  const [isTimeDropdownOpen, setIsTimeDropdownOpen] = useState<boolean>(false);
  const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);
  const clubDropdownRef = useRef<HTMLDivElement>(null);
  const timeDropdownRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const { data: clubs = [] } = useQuery<ClubWithMembership[]>({
    queryKey: ["/api/clubs", playerId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/clubs${playerId ? `?playerId=${playerId}` : ''}`);
      return response.json();
    },
    enabled: !!playerId,
  });

  // Fetch player data to check premium status
  const { data: player } = useQuery<Player>({
    queryKey: ["/api/players", playerId],
    queryFn: async () => {
      if (!playerId) return null;
      const response = await apiRequest("GET", `/api/players/${playerId}`);
      return response.json();
    },
    enabled: !!playerId,
  });


  const { data: globalRankings = [], isLoading: globalLoading } = useQuery<PlayerRanking[]>({
    queryKey: ["/api/players/rankings", timeFilter],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/players/rankings?timeFilter=${timeFilter}`);
      return response.json();
    },
    enabled: selectedClub === "global",
    staleTime: 0, // No cache - always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  const { data: clubRankings = [], isLoading: clubLoading } = useQuery<ClubRanking[]>({
    queryKey: ["/api/clubs", selectedClub, "rankings", timeFilter],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/clubs/${selectedClub}/rankings?timeFilter=${timeFilter}&_t=${Date.now()}`);
      return response.json();
    },
    enabled: selectedClub !== "global",
    staleTime: 0, // No cache - always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Fetch historical ELO data for expanded player
  const { data: historicalElo = [], isLoading: historicalLoading } = useQuery({
    queryKey: ["/api/players", expandedPlayer, "elo-history", selectedClub, timeFilter],
    queryFn: async () => {
      if (!expandedPlayer) return [];

      // Use club ELO history if viewing club rankings, global ELO history otherwise
      const endpoint = selectedClub === "global"
        ? `/api/players/${expandedPlayer}/elo-history?limit=50&timeFilter=${timeFilter}`
        : `/api/players/${expandedPlayer}/clubs/${selectedClub}/elo-history?limit=50&timeFilter=${timeFilter}&seasonId=current`;

      const response = await apiRequest("GET", endpoint);
      return response.json();
    },
    enabled: !!expandedPlayer,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  const rankings = selectedClub === "global" ? globalRankings : clubRankings;
  const isLoading = selectedClub === "global" ? globalLoading : clubLoading;

  // Calculate min/max ELO values for dynamic scaling when time-filtered
  const isTimeFiltered = timeFilter !== "all";
  const eloValues = isTimeFiltered ? rankings.map(player =>
    selectedClub === "global" ? player.eloRating : (player as ClubRanking).clubEloRating
  ) : [];
  const minElo = isTimeFiltered && eloValues.length > 0 ? Math.min(...eloValues) : 0;
  const maxElo = isTimeFiltered && eloValues.length > 0 ? Math.max(...eloValues) : 0;
  const eloRange = maxElo - minElo;

  // Helper function to calculate ELO progression bar position and width
  const getEloProgressionBar = (eloValue: number) => {
    if (!isTimeFiltered || eloRange === 0) {
      return { left: '50%', width: '0%', color: '#6b7280' };
    }

    // Use the larger absolute value to determine the scale range
    const maxAbsValue = Math.max(Math.abs(minElo), Math.abs(maxElo));

    // Bar width proportional to the value magnitude
    const barWidth = Math.max(8, (Math.abs(eloValue) / maxAbsValue) * 40); // Min 8% width, max 40%

    if (eloValue >= 0) {
      // Positive ELO: extend from center (50%) to the right
      return {
        left: '50%',
        width: `${barWidth}%`,
        color: '#2563eb'
      };
    } else {
      // Negative ELO: extend from center (50%) to the left
      return {
        left: `${50 - barWidth}%`,
        width: `${barWidth}%`,
        color: '#ef4444'
      };
    }
  };

  // Filter clubs based on user membership only (and exclude pending), and sort alphabetically
  const filteredClubs = clubs
    .filter(club => club.isMember && club.status !== 'pending')
    .sort((a, b) => a.name.localeCompare(b.name));

  const currentClubObj = selectedClub !== "global" ? clubs.find(c => c.id === selectedClub) : null;
  const isStandardClub = currentClubObj?.eloStarting === "standard";

  // Reset to global if non-premium user is viewing club rankings
  useEffect(() => {
    if (selectedClub !== "global" && !player?.isPremium && player !== undefined) {
      setSelectedClub("global");
      localStorage.setItem("rankingSelectedClub", "global");
    }
  }, [selectedClub, player?.isPremium, player]);


  // Get display name for selected club
  const selectedClubName = selectedClub === "global"
    ? t('ranking.global')
    : clubs.find(club => club.id === selectedClub)?.name || t('ranking.selectClub');

  // Time filter options
  const timeOptions = [
    { id: "all", label: t('ranking.filters.time.all') },
    { id: "year", label: t('ranking.filters.time.year') },
    { id: "month", label: t('ranking.filters.time.month') },
    { id: "week", label: t('ranking.filters.time.week') }
  ];

  // No filtering needed for time options - simple dropdown

  // Get display name for selected time filter
  const selectedTimeName = timeOptions.find(option => option.id === timeFilter)?.label || t('ranking.selectTime');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clubDropdownRef.current && !clubDropdownRef.current.contains(event.target as Node)) {
        setIsClubDropdownOpen(false);
      }
      if (timeDropdownRef.current && !timeDropdownRef.current.contains(event.target as Node)) {
        setIsTimeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClubSelect = (clubId: string) => {
    // If selecting a club (not global), check if user is premium
    if (clubId !== "global" && !player?.isPremium) {
      toast({
        title: t('ranking.messages.premiumTitle'),
        description: t('ranking.messages.premiumDesc'),
        variant: "default",
      });
      setIsClubDropdownOpen(false);
      return;
    }

    setSelectedClub(clubId);
    localStorage.setItem("rankingSelectedClub", clubId);
    setIsClubDropdownOpen(false);
  };

  const handleTimeSelect = (timeId: string) => {
    setTimeFilter(timeId);
    localStorage.setItem("rankingTimeFilter", timeId);
    setIsTimeDropdownOpen(false);
  };

  const handlePlayerClick = (playerId: string) => {
    setExpandedPlayer(expandedPlayer === playerId ? null : playerId);
  };

  // Simple ELO history graph component
  const EloHistoryGraph = ({ data }: { data: any[] }) => {
    if (!data || data.length === 0) {
      return (
        <div className="text-center py-4 text-gray-500">
          <p>{t('ranking.stats.noHistory')}</p>
        </div>
      );
    }

    // Reverse data to show oldest to newest (left to right)
    const sortedData = [...data].reverse();

    const maxElo = Math.max(...sortedData.map(d => d.eloRating));
    const minElo = Math.min(...sortedData.map(d => d.eloRating));
    const range = maxElo - minElo || 100;

    // Add padding to viewBox (8% margin on all sides)
    const padding = 8;
    const viewBoxWidth = 100;
    const viewBoxHeight = 100;
    const chartWidth = viewBoxWidth - (padding * 2);
    const chartHeight = viewBoxHeight - (padding * 2);

    // Generate very smooth curve using Catmull-Rom spline interpolation
    // This creates a curve that passes through all points but with very smooth transitions
    const generateSmoothPath = (points: Array<{ x: number; y: number }>) => {
      if (points.length < 2) return '';
      if (points.length === 2) {
        return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
      }

      // Use Catmull-Rom spline for very smooth curves
      // Tension parameter (0.5 = centripetal, creates smoother curves)
      const tension = 0.5;

      let path = `M ${points[0].x} ${points[0].y}`;

      for (let i = 0; i < points.length - 1; i++) {
        const p0 = i > 0 ? points[i - 1] : points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = i < points.length - 2 ? points[i + 2] : points[i + 1];

        // Catmull-Rom to Cubic Bezier conversion
        const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
        const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
        const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
        const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;

        path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
      }

      return path;
    };

    // Calculate points with padding
    const chartPoints = sortedData.map((point, index) => {
      const x = padding + (index / (sortedData.length - 1 || 1)) * chartWidth;
      const y = padding + chartHeight - ((point.eloRating - minElo) / range) * chartHeight;
      return { x, y };
    });

    return (
      <div className="p-4 bg-gray-50 dark:bg-muted rounded-lg w-full">
        <h4 className="text-sm font-semibold text-foreground mb-3">{t('ranking.stats.eloHistory')}</h4>
        <div className="relative h-32 w-full min-w-0">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Grid lines */}
            {[0, 25, 50, 75, 100].map((y, i) => (
              <line
                key={i}
                x1={padding}
                y1={padding + (y / 100) * chartHeight}
                x2={padding + chartWidth}
                y2={padding + (y / 100) * chartHeight}
                stroke={isDark ? "rgba(255,255,255,0.15)" : "#e5e7eb"}
                strokeWidth="0.5"
              />
            ))}

            {/* Area fill with gradient */}
            <defs>
              <linearGradient id="eloGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={isDark ? "#ffffff" : "rgb(30, 64, 175)"} stopOpacity="0.25" />
                <stop offset="100%" stopColor={isDark ? "#ffffff" : "rgb(30, 64, 175)"} stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Generate area path for gradient fill */}
            {(() => {
              const linePath = generateSmoothPath(chartPoints);
              const firstPoint = chartPoints[0];
              const lastPoint = chartPoints[chartPoints.length - 1];
              const bottomY = padding + chartHeight;
              const areaPath = `${linePath} L ${lastPoint.x} ${bottomY} L ${firstPoint.x} ${bottomY} Z`;
              return <path d={areaPath} fill="url(#eloGradient)" />;
            })()}
            {/* Smooth ELO curve */}
            <path
              d={generateSmoothPath(chartPoints)}
              fill="none"
              stroke={isDark ? "#ffffff" : "rgb(30, 64, 175)"}
              strokeWidth="0.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-muted-foreground pl-1">
            <span>{maxElo}</span>
            <span>{Math.round((maxElo + minElo) / 2)}</span>
            <span>{minElo}</span>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-3 grid grid-cols-3 gap-4 text-xs">
          <div className="text-center">
            <div className="font-semibold text-foreground">{maxElo}</div>
            <div className="text-muted-foreground">{t('ranking.stats.peak')}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-foreground">{minElo}</div>
            <div className="text-muted-foreground">{t('ranking.stats.lowest')}</div>
          </div>
          <div className="text-center">
            <div className="font-semibold text-foreground">{data.length}</div>
            <div className="text-muted-foreground">{t('ranking.stats.matches')}</div>
          </div>
        </div>
      </div>
    );
  };

  // Get current user stats
  const currentUser = rankings.find(player => player.id === playerId) || null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-gradient-to-r from-padel-green to-padel-green-light">
          <Skeleton className="h-6 w-24 mb-3 bg-white/20" />
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="text-center">
                <Skeleton className="h-8 w-12 mx-auto mb-1 bg-white/20" />
                <Skeleton className="h-3 w-16 mx-auto bg-white/20" />
              </div>
            ))}
          </div>
        </div>
        <div className="p-4 space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ranking-tab" className="w-full">
      {/* Stats Overview */}
      <section className="p-3 sm:p-4 bg-gradient-to-r from-padel-green to-padel-green-light text-white" data-testid="stats-overview">
        <h2 className="text-lg font-semibold mb-3">{t('ranking.stats.rank')}</h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="relative w-12 h-12 mx-auto mb-1" data-testid="current-rank-circle">
              {(() => {
                const rank = currentUser?.rank || 0;

                let backgroundColor = "#ffffff";
                let textColor = "#374151";
                let borderColor = "#d1d5db";

                if (rank === 1) {
                  backgroundColor = "#eab308"; // yellow-500
                  textColor = "#ffffff";
                  borderColor = "#eab308";
                } else if (rank === 2) {
                  backgroundColor = "#9ca3af"; // gray-400
                  textColor = "#ffffff";
                  borderColor = "#9ca3af";
                } else if (rank === 3) {
                  backgroundColor = "#f97316"; // orange-500
                  textColor = "#ffffff";
                  borderColor = "#f97316";
                }

                // Adjust for inactive players
                const matchesPlayed = selectedClub === "global" ? currentUser?.matchesPlayed : (currentUser as ClubRanking)?.clubMatchesPlayed;
                const isInactive = Number(matchesPlayed) === 0;

                const displayRank = !rank || isInactive ? "-" : rank;

                // Reset colors if inactive
                if (isInactive) {
                  backgroundColor = "#ffffff";
                  textColor = "#374151";
                  borderColor = "#d1d5db";
                }

                return (
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl border-2"
                    style={{
                      backgroundColor,
                      color: textColor,
                      borderColor
                    }}
                  >
                    {displayRank}
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="text-center">
            {(() => {
              const eloRating = currentUser
                ? (selectedClub === "global" ? currentUser.eloRating : (currentUser as ClubRanking).clubEloRating)
                : 1500;
              const isTimeFiltered = timeFilter !== "all";
              const { currentTier, nextTier, progress, isMaxLevel } = getEloProgress(eloRating);

              return (
                <div data-testid="elo-progress">
                  {isTimeFiltered ? (
                    // Time-filtered view: Show ELO progression
                    <div>
                      <div className="text-xs font-semibold text-white mb-2 text-center">
                        {(() => {
                          return (
                            <span style={{ color: '#ffffff' }}>
                              {eloRating > 0 ? `+${eloRating}` : `${eloRating}`} ELO
                            </span>
                          );
                        })()}
                      </div>

                      {/* Only show progress bar if there is a change */}
                      {eloRating !== 0 ? (
                        <div className="w-full bg-white/20 rounded-full h-2 mb-2 relative">
                          {/* Center line */}
                          <div className="absolute left-1/2 top-0 w-px h-full bg-white/40 transform -translate-x-1/2"></div>
                          {/* Progression bar */}
                          {(() => {
                            const barStyle = getEloProgressionBar(eloRating);
                            return (
                              <div
                                className="h-2 rounded-full transition-all duration-300 absolute top-0"
                                style={{
                                  left: barStyle.left,
                                  width: barStyle.width,
                                  backgroundColor: '#ffffff'
                                }}
                              ></div>
                            );
                          })()}
                        </div>
                      ) : (
                        // Simple trait for 0 progression
                        <div className="h-2 mb-2 flex items-center justify-center">
                          <div className="w-8 h-0.5 bg-white/40 rounded"></div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // All-time view: Show current tier
                    <div>
                      <div className="text-xs font-semibold text-white mb-2">
                        {currentTier
                          ? (isStandardClub
                            ? `${t('ranking.tiers.level')} ${currentTier.level}`
                            : `${t('ranking.tiers.' + currentTier.name)} (${currentTier.level})`)
                          : t('ranking.messages.unknown')}
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-2 mb-2">
                        <div
                          className="bg-white h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <div className="text-xs opacity-90">
                        {isMaxLevel ? `${eloRating}` : `${eloRating}/${currentTier?.max || "?"}`}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="text-center">
            <div className="relative w-12 h-12 mx-auto mb-1" data-testid="win-rate-progress">
              <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                {/* Background circle */}
                <path
                  className="text-white/20"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                {/* Progress circle */}
                <path
                  className="text-white"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  fill="none"
                  strokeDasharray={`${currentUser
                    ? (selectedClub === "global" ? currentUser.winRate : (currentUser as ClubRanking).clubWinRate)
                    : 0
                    }, 100`}
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              {/* Win rate percentage in center */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {currentUser
                    ? (selectedClub === "global" ? currentUser.winRate : (currentUser as ClubRanking).clubWinRate)
                    : 0
                  }%
                </span>
              </div>
            </div>
            <div className="text-xs opacity-90">
              {t('ranking.stats.winRate')} ({currentUser
                ? (selectedClub === "global" ? currentUser.matchesPlayed : (currentUser as ClubRanking).clubMatchesPlayed)
                : 0
              })
            </div>
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="p-3 sm:p-4" data-testid="leaderboard-section">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-foreground mb-3">
            {selectedClub === "global" ? t('ranking.leaderboard.global') : t('ranking.leaderboard.club')}
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {/* Club Dropdown */}
            <div className="relative flex-1" ref={clubDropdownRef} id="tour-ranking-scope">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsClubDropdownOpen(!isClubDropdownOpen)}
                  className="w-full text-left px-3 py-2 text-sm bg-gray-100 dark:bg-input border border-gray-300 dark:border-border rounded-lg pr-10 cursor-pointer hover:bg-gray-200 dark:hover:bg-muted transition-colors flex items-center justify-between"
                  data-testid="button-club-dropdown"
                >
                  <span>{selectedClubName}</span>
                  {selectedClub !== "global" && (
                    <Crown className="w-3 h-3 text-yellow-500" />
                  )}
                </button>
                <ChevronDown
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm transition-transform ${isClubDropdownOpen ? 'rotate-180' : ''
                    }`}
                />
              </div>

              {/* Dropdown Options */}
              {isClubDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-popover border border-gray-300 dark:border-border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  <div
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-muted ${selectedClub === "global" ? "bg-padel-green text-white hover:bg-padel-green" : ""
                      }`}
                    onClick={() => handleClubSelect("global")}
                    data-testid="option-global-rankings"
                  >
                    {t('ranking.global')}
                  </div>
                  {filteredClubs.map((club) => {
                    const isPremiumFeature = !player?.isPremium;
                    return (
                      <div
                        key={club.id}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-muted flex items-center justify-between ${selectedClub === club.id ? "bg-padel-green text-white hover:bg-padel-green" : ""
                          } ${isPremiumFeature ? "opacity-60" : ""}`}
                        onClick={() => handleClubSelect(club.id)}
                        data-testid={`option-club-${club.id}`}
                      >
                        <span className="flex items-center space-x-2">
                          {club.name}
                          <Crown className="w-3 h-3 text-yellow-500" />
                        </span>
                        {isPremiumFeature && (
                          <Lock className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Time Period Dropdown */}
            <div className="relative flex-1" ref={timeDropdownRef} id="tour-ranking-filters">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsTimeDropdownOpen(!isTimeDropdownOpen)}
                  className="w-full text-left text-sm bg-gray-100 dark:bg-input border border-gray-300 dark:border-border rounded-lg px-3 py-2 pr-10 cursor-pointer hover:bg-gray-200 dark:hover:bg-muted transition-colors"
                  data-testid="button-time-dropdown"
                >
                  {selectedTimeName}
                </button>
                <ChevronDown
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm transition-transform ${isTimeDropdownOpen ? 'rotate-180' : ''
                    }`}
                />
              </div>

              {/* Dropdown Options */}
              {isTimeDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-popover border border-gray-300 dark:border-border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  {timeOptions.map((option) => (
                    <div
                      key={option.id}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-muted ${timeFilter === option.id ? "bg-padel-green text-white hover:bg-padel-green" : ""
                        }`}
                      onClick={() => handleTimeSelect(option.id)}
                      data-testid={`option-time-${option.id}`}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2" data-testid="rankings-list">
          {rankings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('ranking.messages.noRankings')}</p>
              <p className="text-sm mt-1">{t('ranking.messages.playToSee')}</p>
            </div>
          ) : (
            rankings.map((player, index) => {
              const isCurrentUser = player.id === playerId;
              let bgClass = "bg-white dark:bg-card border border-gray-200 dark:border-border";
              let rankBg = "bg-gray-200 dark:bg-muted text-gray-700 dark:text-foreground";
              let icon = null;

              // Adjust for inactive players
              const matchesPlayed = selectedClub === "global" ? player.matchesPlayed : (player as ClubRanking).clubMatchesPlayed;
              const isInactive = Number(matchesPlayed) === 0;

              // Keep all banners with white background, only color the ranking circles
              // Only apply ranking colors if active
              if (!isInactive) {
                if (player.rank === 1) {
                  icon = <Crown className="text-yellow-500 text-sm mb-1" />;
                } else if (player.rank === 2) {
                  icon = <Medal className="text-gray-500 text-sm mb-1" />;
                } else if (player.rank === 3) {
                  icon = <Medal className="text-orange-500 text-sm mb-1" />;
                }
              }

              // Gray out inactive players
              if (isInactive) {
                bgClass += " opacity-60 grayscale";
              }

              // Apply green outline for current user regardless of rank
              if (isCurrentUser) {
                bgClass = bgClass.replace('border border-gray-200 dark:border-border', 'border-2 border-padel-green');
              }

              const playerEloRating = selectedClub === "global" ? player.eloRating : (player as ClubRanking).clubEloRating;
              const playerWinRate = selectedClub === "global" ? player.winRate : (player as ClubRanking).clubWinRate;
              const playerMatchesPlayed = selectedClub === "global" ? player.matchesPlayed : (player as ClubRanking).clubMatchesPlayed;

              // For time-filtered views, show ELO progression instead of current tier
              const isTimeFiltered = timeFilter !== "all";
              const { currentTier, progress, isMaxLevel } = getEloProgress(playerEloRating);

              return (
                <div key={player.id} id={index === 0 ? "tour-ranking-list" : undefined}>
                  <div
                    className={`rounded-lg p-3 flex items-center space-x-3 cursor-pointer hover:shadow-md transition-shadow ${bgClass}`}
                    data-testid={`ranking-player-${player.id}`}
                    onClick={() => handlePlayerClick(player.id)}
                  >
                    {/* Rank Circle */}
                    <div className="flex-shrink-0">
                      {(() => {
                        let rankBgColor = "bg-white dark:bg-muted text-gray-700 dark:text-foreground border-gray-300 dark:border-border";
                        let rankBorderColor = "border-gray-300 dark:border-border";

                        let backgroundColor = "var(--muted)";
                        let textColor = "var(--foreground)";
                        let borderColor = "var(--border)";

                        if (!isInactive) {
                          if (player.rank === 1) {
                            backgroundColor = "#eab308"; // yellow-500
                            textColor = "#ffffff";
                            borderColor = "#eab308";
                          } else if (player.rank === 2) {
                            backgroundColor = "#9ca3af"; // gray-400
                            textColor = "#ffffff";
                            borderColor = "#9ca3af";
                          } else if (player.rank === 3) {
                            backgroundColor = "#f97316"; // orange-500
                            textColor = "#ffffff";
                            borderColor = "#f97316";
                          }
                        }

                        return (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg border-2"
                            style={{
                              backgroundColor,
                              color: textColor,
                              borderColor
                            }}
                          >
                            {isInactive ? "-" : player.rank}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Player Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-foreground truncate" data-testid={`text-player-name-${player.id}`}>
                        {isCurrentUser ? t('ranking.youWithName', { name: player.name }) : player.name}
                      </div>

                      {/* ELO Progress Bar */}
                      <div className="mt-1">
                        {isTimeFiltered ? (
                          // Time-filtered view: Show ELO progression
                          <div>
                            <div className="text-xs font-medium text-muted-foreground mb-1 text-center">
                              {(() => {
                                const barStyle = getEloProgressionBar(playerEloRating);
                                return (
                                  <span style={{ color: barStyle.color }}>
                                    {playerEloRating > 0 ? `+${playerEloRating}` : `${playerEloRating}`} ELO
                                  </span>
                                );
                              })()}
                            </div>
                            {playerEloRating !== 0 ? (
                              <div className="w-full bg-gray-200 dark:bg-muted rounded-full h-1.5 relative">
                                {/* Center line */}
                                <div className="absolute left-1/2 top-0 w-px h-full bg-gray-400 dark:bg-muted-foreground transform -translate-x-1/2"></div>
                                {/* Progression bar */}
                                {(() => {
                                  const barStyle = getEloProgressionBar(playerEloRating);
                                  return (
                                    <div
                                      className="h-1.5 rounded-full transition-all duration-300 absolute top-0"
                                      style={{
                                        left: barStyle.left,
                                        width: barStyle.width,
                                        backgroundColor: barStyle.color
                                      }}
                                    ></div>
                                  );
                                })()}
                              </div>
                            ) : (
                              // Simple trait for 0 progression
                              <div className="h-1.5 flex items-center justify-center">
                                <div className="w-6 h-0.5 bg-gray-300 dark:bg-muted-foreground rounded"></div>
                              </div>
                            )}
                          </div>
                        ) : (
                          // All-time view: Show current tier
                          <div>
                            <div className="text-xs font-medium text-foreground mb-1">
                              {currentTier
                                ? (isStandardClub
                                  ? `${t('ranking.tiers.level')} ${currentTier.level}`
                                  : `${t('ranking.tiers.' + currentTier.name)} (${currentTier.level})`)
                                : t('ranking.messages.unknown')}
                              <span className="text-muted-foreground ml-1">
                                {isMaxLevel ? `${playerEloRating}` : `${playerEloRating}/${currentTier?.max || "?"}`}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-muted rounded-full h-1.5">
                              <div
                                className="h-1.5 rounded-full transition-all duration-300"
                                style={{
                                  width: `${progress}%`,
                                  backgroundColor: isDark ? 'rgb(96, 165, 250)' : 'rgb(30, 64, 175)'
                                }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Win Rate Circle */}
                    <div className="flex-shrink-0 text-center">
                      <div className="relative w-10 h-10 mx-auto mb-1" data-testid={`win-rate-progress-${player.id}`}>
                        <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 36 36">
                          {/* Background circle */}
                          <path
                            className="text-muted"
                            stroke="currentColor"
                            strokeWidth="3"
                            fill="none"
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          {/* Progress circle */}
                          <path
                            stroke={isDark ? "#ffffff" : "var(--padel-green)"}
                            strokeWidth="3"
                            strokeLinecap="round"
                            fill="none"
                            strokeDasharray={`${playerWinRate}, 100`}
                            d="M18 2.0845
                              a 15.9155 15.9155 0 0 1 0 31.831
                              a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        {/* Win rate percentage in center */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[10px] font-bold text-foreground">
                            {playerWinRate}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Expand Chevron attached to the right */}
                    <div className="flex-shrink-0 ml-1">
                      <ChevronDown
                        className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expandedPlayer === player.id ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </div>

                  {/* Expanded view (ELO History) */}
                  {expandedPlayer === player.id && (
                    <div className="bg-gray-50 dark:bg-muted border-x border-b border-gray-200 dark:border-border rounded-b-lg p-2 mb-2 animate-in slide-in-from-top-2">
                      {historicalLoading ? (
                        <div className="p-4 flex justify-center">
                          <Skeleton className="h-32 w-full" />
                        </div>
                      ) : (
                        <EloHistoryGraph data={historicalElo || []} />
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
